/**
 * CF-V11-CTA-MORPH-V1: pure UI derivers (deriveCtaCopy + affordability helpers).
 *
 * All math takes BigInt nanoTON. No Number coercion. Tier config sourced
 * from backend /api/v1/balance response (NEVER local constants in
 * real-money mode per Scorer R4 SIGN binding 9).
 *
 * Stable base summary line "Bet X → Win 2X TON" — the 50:50 trust
 * anchor per PL #5875 — NEVER changes regardless of tier selection.
 * Only the FLIP button morphs to show tier-specific cost + win.
 */

import type { BoostTier, TierConfigPayload, TierConfigEntry } from './boost-tier-config';
import { translate } from '../i18n';

export const NANO_PER_TON = 1_000_000_000n;

export function findTierEntry(cfg: TierConfigPayload, tier: BoostTier): TierConfigEntry | null {
  return cfg.tiers.find((t) => t.key === tier) ?? null;
}

/** Compute fee in nano: stake × fee_bps / 10000 (floor matches backend). */
export function computeFeeNano(stakeNano: bigint, feeBps: number): bigint {
  return (stakeNano * BigInt(feeBps)) / 10000n;
}

/** Compute gross cost = stake + fee. */
export function computeGrossCostNano(stakeNano: bigint, feeBps: number): bigint {
  return stakeNano + computeFeeNano(stakeNano, feeBps);
}

/** Compute payout-on-win: stake × multiplier_bps / 10000. */
export function computePayoutOnWinNano(stakeNano: bigint, multiplierBps: number): bigint {
  return (stakeNano * BigInt(multiplierBps)) / 10000n;
}

/** Format BigInt nano → TON display string (trims trailing zeros, e.g. "2.20" → "2.2", "1.0" → "1").
 *
 * Shows up to 3 decimal digits so fees like 0.5 × 0.15 = 0.075 TON display as
 * "0.075" (exact), not "0.07" (the prior 2-decimal truncation misled users into
 * thinking the per-TON rate was 0.14 instead of the documented 0.15). 3 decimals
 * covers every chip × tier combination exactly (no truncation for documented
 * inputs) — see frontend-ui-derivers.spec.ts boost/mega rows for the proof set.
 * Trailing zeros are still trimmed so "0.200" → "0.2", "0.150" → "0.15".
 */
export function fmtTon(nano: bigint): string {
  const whole = nano / NANO_PER_TON;
  const frac = nano % NANO_PER_TON;
  if (frac === 0n) return whole.toString();
  // Up to 3 decimal digits, trim trailing zeros
  const fracStr = frac.toString().padStart(9, '0').slice(0, 3);
  const trimmed = fracStr.replace(/0+$/, '');
  return trimmed.length === 0 ? whole.toString() : `${whole}.${trimmed}`;
}

/**
 * Affordability check for a tier+stake combo. Returns true if the user has
 * enough balance to cover GROSS cost (stake + fee). Standard fee=0 → equivalent
 * to balance ≥ stake; Boost/Mega requires balance ≥ stake + fee.
 *
 * Per Scorer R3 correction #4: UI MUST gate on stake+fee, not just stake.
 */
export function canAffordTier(balanceNano: bigint, stakeNano: bigint, tierCfg: TierConfigEntry): boolean {
  const gross = computeGrossCostNano(stakeNano, tierCfg.fee_bps);
  return balanceNano >= gross;
}

/**
 * Derives the FLIP button text per PL #5875 morphing design.
 *
 *   Standard: "🪙 FLIP {stake} TON"
 *   Boost:    "⚡️ Boost +{scaledFee} → Win {scaledWin} TON"
 *   Mega:    "⚡️⚡️ Mega +{scaledFee} → Win {scaledWin} TON"
 *
 * stake and tier are required; tierConfig is REQUIRED in real-money mode.
 * Throws if tierConfig is null/undefined OR missing the requested tier —
 * fail-closed per Scorer R4 SIGN binding 9.
 */
export function deriveCtaCopy(stakeNano: bigint, tier: BoostTier, tierConfig: TierConfigPayload | null): string {
  if (!tierConfig) {
    throw new Error('deriveCtaCopy: tierConfig is required (no local fallback in real-money mode)');
  }
  const cfg = findTierEntry(tierConfig, tier);
  if (!cfg) {
    throw new Error(`deriveCtaCopy: tier "${tier}" not found in tier_config`);
  }
  if (tier === 'standard') {
    return translate('cta.flip_standard', { stake: fmtTon(stakeNano) });
  }
  const fee = computeFeeNano(stakeNano, cfg.fee_bps);
  const win = computePayoutOnWinNano(stakeNano, cfg.multiplier_bps);
  const prefix = tier === 'boost' ? '⚡️' : '⚡️⚡️';
  return translate('cta.flip_boost_mega', { prefix, label: translate('tier.' + tier), fee: fmtTon(fee), win: fmtTon(win) });
}

/**
 * Derives the stable base summary line "Bet X → Win 2X TON" — the 50:50
 * trust anchor that NEVER changes with tier selection. ALWAYS uses ×2
 * (Standard win), independent of tier_config, by design (PL #5875).
 */
export function deriveBaseSummary(stakeNano: bigint): string {
  return translate('cta.base_summary', { stake: fmtTon(stakeNano), stake_x2: fmtTon(stakeNano * 2n) });
}
